import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {Sun, Moon, Github, Linkedin, ChevronDown, Globe } from 'lucide-react';

import { useLanguage } from '../LanguageContext';
import { Language } from '../translations'
import SubstackButton from './SubstackButton';

// Mobile menu toggle: three slightly uneven hand-drawn strokes that
// redraw into a hand-drawn cross when open (a chevron read as "expand
// this" rather than "menu"). Same stroke language as the site's other
// drawn marks; the paths morph via a CSS transition on `d`.
const MenuIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg width="26" height="26" viewBox="0 0 26 26" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" aria-hidden="true" className="menu-icon">
    <path d={open ? 'M6.5 6.8 C11 11, 15 15.2, 19.6 19.4' : 'M4.5 8 C10 7.3, 16 7.6, 21.5 7.2'} />
    <path d={open ? 'M13 13 C13 13, 13 13, 13 13' : 'M4.8 13.2 C10.5 12.8, 15.5 13.5, 20.2 13'} style={{ opacity: open ? 0 : 1 }} />
    <path d={open ? 'M19.4 6.6 C15 11, 11 15, 6.7 19.5' : 'M4.5 18.4 C9 18, 13.8 18.6, 17.5 18.2'} />
  </svg>
);


const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const location = useLocation();
  const { t, setLanguage, language } = useLanguage(); // Use the custom hook

  const languages: Language[] = ['en', 'nl'];

  // Touch browsers fire mouseenter on tap (and never a matching
  // mouseleave), so the JS hover color below would stick on a phone/tablet.
  // Same query as tailwind.config.js's hoverOnlyWhenSupported.
  const canHover = () =>
    typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Close the mobile menu (and its language dropdown) whenever the route
  // changes, not only when one of its own links is tapped.
  useEffect(() => {
    setIsOpen(false);
    setIsLanguageDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const navItems = [
    { name: t('navbar.about'), path: '/' },
    { name: t('navbar.articles'), path: '/articles' },
    { name: t('navbar.experience'), path: '/experience' },
  ];

  const socialIcons = [
    { Icon: Github, link: 'https://github.com/Pieter-Jacobs', label: t('navbar.aria.github') },
    { Icon: Linkedin, link: 'https://www.linkedin.com/in/pieter-jacobs-13a3321a3/', label: t('navbar.aria.linkedin') },
  ];


  return (
    <nav className="fixed top-0 left-0 w-full z-50 transition-all duration-300" style={{backgroundColor: 'var(--color-notebook)'}}>
      {/* The full desktop bar only fits from lg up: at md (768px) the
          three labels wrapped onto two lines each and the right-hand
          icons overflowed the viewport, so tablets get the menu too. */}
      <div className="mx-auto px-2 sm:px-2 md:px-4 lg:px-6 h-full">
        <div className="flex lg:grid lg:grid-cols-[1fr_auto_1fr] items-center h-16 justify-between">
          <div className="flex items-center">
            <Link to="/" aria-label={t('navbar.aria.home')} className="w-[50px] font-bold text-[var(--color-text-primary)]">
              <img src="/images/Logo.png" className="dark:filter dark:invert-[.5]" alt="Pieter Jacobs logo" />
            </Link>
          </div>
          <div className="hidden lg:block">
            <div className="flex items-baseline space-x-8">
              {navItems.map((item) => {
                // Scales the circle's tilt down for longer nav labels —
                // a fixed angle read as a diagonal slant once labels got
                // past a short word, same fix as the in-prose link marker.
                const circleRotateDeg = -Math.max(0.3, 2 - Math.max(0, item.name.length - 6) * 0.12);
                return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`nav-circle-link relative px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                    location.pathname === item.path
                      ? 'text-[var(--color-text-primary)]'
                      : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                    }`}
                  style={{
                    ...(location.pathname === item.path ? {color: 'var(--color-main-accent)'} : {}),
                    ['--circle-rotate' as any]: `${circleRotateDeg}deg`,
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== item.path && canHover()) {
                      e.currentTarget.style.color = 'var(--color-main-accent)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== item.path) {
                      e.currentTarget.style.color = '';
                    }
                  }}
                >
                  {item.name}
                </Link>
                );
              })}
            </div>
          </div>
          <button
            type="button"
            className="lg:hidden flex items-center justify-center w-11 h-11 -mr-1 text-[var(--color-text-primary)]"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
            onClick={() => { setIsOpen(!isOpen); setIsLanguageDropdownOpen(false); }}>
            <MenuIcon open={isOpen} />
          </button>
          <div className="hidden lg:flex items-center lg:space-x-4 justify-end">
            {socialIcons.map(({ Icon, link, label }) => (
              <a 
                key={link}
                href={link}
                aria-label={label}
target="_blank" 
                rel="noopener noreferrer" 
                className="text-[var(--color-text-primary)] transition-colors duration-300"
                onMouseEnter={(e) => { if (canHover()) e.currentTarget.style.color = 'var(--color-main-accent)'; }}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-text-primary)'}
              >
                <Icon className="w-6 h-6 md:w-4 md:h-4 lg:w-5 lg:h-5" />
              </a>
            ))}
            <div>
              <SubstackButton label={t('navbar.subscribe')} />
            </div>
            <div className="relative">
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                aria-label={t('navbar.aria.language')}
                aria-expanded={isLanguageDropdownOpen}
                className="flex items-center space-x-1 lg:p-2 rounded-full text-[var(--color-text-primary)] hover:bg-[var(--color-card-bg)]"
              >
                <Globe className="w-6 h-6 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                <span className="ml-1">{language.toUpperCase()}</span>
                <ChevronDown size={16} />
              </button>
              {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 py-2 w-48 rounded-md shadow-xl z-20" style={{backgroundColor: 'var(--color-notebook-white)'}}>
                  {languages.map((lang) => (
                    <button
                      key={lang}
                      className="flex items-center px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-card-bg)] w-full"
                      onClick={() => {
                        setLanguage(lang);
                        setIsLanguageDropdownOpen(false)
                      }}
                    >
                      <Globe size={16} className="mr-2" />
                      <span className="mr-2">{lang.toUpperCase()}</span>
                      {t(`navbar.languageName.${lang}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              aria-label={t('navbar.aria.darkMode')}
              aria-pressed={darkMode}
              className="lg:p-2 rounded-full text-[var(--color-text-primary)] hover:bg-[var(--color-card-bg)]"
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      {/* Mobile/tablet menu: a plain list of handwritten page names, with
          the nav circle drawn around the current page only (the same "you
          are here" gesture as desktop). No hover color or row background —
          on touch those stuck on after a tap — and the circle sits on an
          inline span so it hugs the label instead of stretching into a
          full-width ellipse around the whole row. */}
      {isOpen && (
        <div className="lg:hidden border-b border-[var(--color-border)]">
          <div className="px-4 pt-1 pb-3 sm:px-6">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const circleRotateDeg = -Math.max(0.3, 2 - Math.max(0, item.name.length - 6) * 0.12);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className="block py-3 text-lg text-[var(--color-text-primary)]"
                  onClick={() => setIsOpen(false)}
                >
                  <span
                    className="nav-circle-link inline-block"
                    style={{
                      ...(isActive ? {color: 'var(--color-main-accent)'} : {}),
                      ['--circle-rotate' as any]: `${circleRotateDeg}deg`,
                    }}
                  >
                    {item.name}
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="px-4 sm:px-6 py-2 flex justify-between items-center border-t border-[var(--color-border)]">
            <div className="flex -ml-2">
              {socialIcons.map(({ Icon, link, label }) => (
                <a
                  key={link}
                  href={link}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 text-[var(--color-text-primary)]"
                >
                  <Icon size={20} />
                </a>
              ))}
            </div>
            <div>
              <SubstackButton label={t('navbar.subscribe')} />
            </div>
            <div className="flex items-center space-x-2">
              <div className="relative">
                <button
                  onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)
                  }
                  aria-label={t('navbar.aria.language')}
                  aria-expanded={isLanguageDropdownOpen}
                  className="flex items-center space-x-1 p-2 rounded-full text-[var(--color-text-primary)] hover:bg-[var(--color-card-bg)]"
                >
                  <Globe size={20} />
                  <span>{language.toUpperCase()}</span>
                  <ChevronDown size={16} />
                </button>
                {isLanguageDropdownOpen && (
                  <div className="absolute right-0 mt-2 py-2 w-48 rounded-md shadow-xl z-20" style={{backgroundColor: 'var(--color-notebook-white)'}}>
                    {languages.map((lang) => (
                      <button
                        key={lang}
                        className="flex items-center px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-card-bg)] w-full"
                        onClick={() => {
                          setLanguage(lang)
                          setIsLanguageDropdownOpen(false)
                        }
                        }
                      >
                        <Globe size={16} className="mr-2" />
                        <span className="mr-2">{lang.toUpperCase()}</span>
                        {t(`navbar.languageName.${lang}`)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => setDarkMode(!darkMode)}
                aria-label={t('navbar.aria.darkMode')}
                aria-pressed={darkMode}
                className="flex items-center justify-center w-10 h-10 rounded-full text-[var(--color-text-primary)] hover:bg-[var(--color-card-bg)]"
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;